import { supabase } from '../lib/supabase';

const CARD_COVER = { width: 640, height: 512, quality: 75, resize: 'cover' };
const CARD_AVATAR = { width: 160, height: 160, quality: 80, resize: 'cover' };

/** Stable user id used in portfolio_images.photographer_id */
export function getPhotographerUserId(photographer) {
  if (!photographer) return null;
  return photographer.user_id || photographer.userId || photographer.id || null;
}

/**
 * Convert Supabase public object URL → render/transform URL for card-sized images.
 * Non-storage URLs are returned unchanged.
 */
export function toOptimizedImageUrl(
  url,
  { width = 640, height, quality = 75, resize = 'cover' } = {},
) {
  if (!url || typeof url !== 'string') return url;

  const objectMarker = '/storage/v1/object/public/';
  const idx = url.indexOf(objectMarker);
  if (idx === -1) return url;

  const base = url.slice(0, idx);
  const pathAfterBucket = url.slice(idx + objectMarker.length);
  const params = new URLSearchParams({
    width: String(width),
    quality: String(quality),
    resize,
  });
  if (height) params.set('height', String(height));

  return `${base}/storage/v1/render/image/public/${pathAfterBucket}?${params.toString()}`;
}

export function getCardCoverUrl(rawUrl) {
  return rawUrl ? toOptimizedImageUrl(rawUrl, CARD_COVER) : null;
}

export function getCardAvatarUrl(rawUrl) {
  if (!rawUrl || typeof rawUrl !== 'string') return rawUrl;
  if (rawUrl.includes('ui-avatars.com') || rawUrl.includes('rocket.new')) return rawUrl;
  return toOptimizedImageUrl(rawUrl, CARD_AVATAR);
}

/**
 * Batch-fetch one cover image per photographer (featured first, then newest).
 * @returns {Map<string, { imageUrl: string, alt: string }>}
 */
export async function fetchPortfolioCoverMap(photographerIds) {
  const uniqueIds = [...new Set((photographerIds || []).filter(Boolean))];
  if (uniqueIds.length === 0) return new Map();

  const { data, error } = await supabase
    .from('portfolio_images')
    .select('photographer_id, image_url, description, title, is_featured, created_at')
    .in('photographer_id', uniqueIds)
    .eq('approval_status', 'approved');

  if (error) {
    console.warn('fetchPortfolioCoverMap:', error.message);
    return new Map();
  }

  const sorted = [...(data || [])].sort((a, b) => {
    if (a.is_featured !== b.is_featured) return a.is_featured ? -1 : 1;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  const map = new Map();
  for (const row of sorted) {
    if (map.has(row.photographer_id)) continue;
    map.set(row.photographer_id, {
      imageUrl: row.image_url,
      alt: row.description || row.title || 'صورة من معرض أعمال المصور',
    });
  }
  return map;
}

export function applyCoverFromMap(photographer, coverMap) {
  const userId = getPhotographerUserId(photographer);
  const cover = userId ? coverMap.get(userId) : null;
  const displayName =
    photographer.name ||
    photographer.user?.fullName ||
    photographer.user?.full_name ||
    'مصور محترف';

  const rawAvatar =
    photographer.avatar ||
    photographer.user?.avatarUrl ||
    photographer.user?.avatar_url ||
    null;

  return {
    ...photographer,
    portfolioImage: cover ? getCardCoverUrl(cover.imageUrl) : null,
    portfolioImageOriginal: cover?.imageUrl || null,
    portfolioImageAlt: cover?.alt || `صورة من معرض أعمال ${displayName}`,
    avatar: rawAvatar ? getCardAvatarUrl(rawAvatar) : photographer.avatar,
    avatarOriginal: rawAvatar || null,
  };
}

/** Attach optimized cover + avatar URLs for a list of card-shaped photographers. */
export async function attachPortfolioCovers(photographers, photographerIds = null) {
  const ids =
    photographerIds ||
    photographers.map((p) => getPhotographerUserId(p)).filter(Boolean);
  const coverMap = await fetchPortfolioCoverMap(ids);
  return photographers.map((p) => applyCoverFromMap(p, coverMap));
}
